import type { FieldJob } from "../types/contract";

export const isActiveJob = (j: Pick<FieldJob, "status">): boolean =>
  j.status === "waiting" || j.status === "in_progress";

// Blocked stays on the active read: a blocked job can return to the queue and
// then finish. The completion refresh treats that the same as running or queued.
export const wasInFlight = (j: Pick<FieldJob, "status">): boolean =>
  isActiveJob(j) || j.status === "blocked";

// Live remaining seconds: `eta_seconds` is as-of `eta_updated_at`, so subtract the time elapsed since the
// estimator last wrote it (spec §"Estimator": the FE counts down between ticks). Clamped ≥ 0 — a job past
// its estimate shows "finishing up", never a negative. NULL eta (terminal/blocked/pre-first-tick) → null.
export function etaRemainingS(
  job: Pick<FieldJob, "eta_seconds" | "eta_updated_at">,
  nowMs: number,
): number | null {
  if (job.eta_seconds == null) return null;
  const asOf = job.eta_updated_at ? Date.parse(job.eta_updated_at) : NaN;
  const elapsedS = Number.isFinite(asOf)
    ? Math.max(0, (nowMs - asOf) / 1000)
    : 0;
  return Math.max(0, Math.round(job.eta_seconds - elapsedS));
}

// Rounding CARRIES across unit boundaries: seconds that round to 60 promote to "~1 min" and minutes
// are rounded FIRST then split into h/m — so 3599 s is "~1 h", never "~60 min", and 7185 s is "~2
// h", never the impossible "~1 h 60 min" every countdown used to pass through at each hour
// boundary.
export function fmtEta(s: number | null): string {
  if (s == null) return "";
  if (s < 5) return "finishing up…";
  const r = Math.round(s / 5) * 5;
  if (s < 60 && r < 60) return `~${r} s`;
  const totalMin = Math.max(1, Math.round(s / 60));
  if (totalMin < 60) return `~${totalMin} min`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return m ? `~${h} h ${m} min` : `~${h} h`;
}

// What the pane renders, in display order: running first (the thing happening now), the queue in
// queue_position order, blocked, then RECENT failures (the why-didn't-my-map-appear answer).
export interface JobsView {
  running: FieldJob[];
  waiting: FieldJob[];
  blocked: FieldJob[];
  failed: FieldJob[];
}
export const FAILED_VISIBLE_MS = 24 * 3600 * 1000;
export function jobsView(jobs: FieldJob[], nowMs: number): JobsView {
  const running: FieldJob[] = [];
  const waiting: FieldJob[] = [];
  const blocked: FieldJob[] = [];
  const failed: FieldJob[] = [];
  for (const j of jobs) {
    if (j.status === "in_progress") running.push(j);
    else if (j.status === "waiting") waiting.push(j);
    else if (j.status === "blocked") blocked.push(j);
    else if (j.status === "failed") {
      const doneAt = Date.parse(j.completed_at ?? j.created_at);
      if (!Number.isFinite(doneAt) || nowMs - doneAt <= FAILED_VISIBLE_MS)
        failed.push(j);
    }
  }
  waiting.sort((a, b) => (a.queue_position ?? 1e9) - (b.queue_position ?? 1e9));
  return { running, waiting, blocked, failed };
}

export const jobsViewEmpty = (v: JobsView): boolean =>
  v.running.length + v.waiting.length + v.blocked.length + v.failed.length ===
  0;

// While a job is still moving, poll every 2.5 seconds. A blocked job can return to
// waiting, so it keeps the same interval. When nothing is active, do not poll.
export const POLL_ACTIVE_MS = 2_500;
export function pollIntervalFor(jobs: FieldJob[] | undefined): number | false {
  if (jobs?.some(wasInFlight)) return POLL_ACTIVE_MS;
  return false;
}

// Jobs that were in flight and are now complete. The first snapshot has no
// prior in-flight set, so opening a field does not refresh for work that
// already finished. A failure is not a completion: the screens did not gain data.
export function newlyCompleted(
  prev: FieldJob[] | undefined,
  next: FieldJob[],
): FieldJob[] {
  if (!prev?.length) return [];
  const wasMoving = new Set(prev.filter(wasInFlight).map((j) => j.job_id));
  return next.filter((j) => j.status === "complete" && wasMoving.has(j.job_id));
}

// Human label: server-humanized name when present, else the process id de-underscored.
export const jobLabel = (j: FieldJob): string =>
  (j.process_name && j.process_name.trim()) || j.process_id.replace(/_/g, " ");

// rule: with MORE THAN ONE process active (running OR queued — the queue counts), don't name a
// single job; say "multiple" and give the ETA to get them ALL done, i.
export interface JobsSummary {
  count: number;
  anyRunning: boolean;
  /* * "Covariate build" for one, "Multiple processes running/queued" for several */
  label: string;
  /* * seconds until ALL active jobs are done, or null when nothing reports an ETA */
  allDoneS: number | null;
  /* * the right-hand detail: "~20 min left" / "~1 h for all" / "#2 in line" / "running" / "queued" */
  detail: string;
}

export function fieldJobsSummary(
  jobs: FieldJob[],
  nowMs: number,
): JobsSummary | null {
  const active = jobs.filter(isActiveJob);
  if (!active.length) return null;
  const running = active.filter((j) => j.status === "in_progress");
  const anyRunning = running.length > 0;

  // max, not sum — see the note above
  const etas = active
    .map((j) => etaRemainingS(j, nowMs))
    .filter((s): s is number => s != null);
  const allDoneS = etas.length ? Math.max(...etas) : null;

  if (active.length === 1) {
    const j = active[0];
    const detail =
      j.status === "in_progress"
        ? allDoneS != null
          ? `${fmtEta(allDoneS)} left`
          : "running"
        : j.queue_position != null
          ? `#${j.queue_position} in line`
          : "queued";
    return {
      count: 1,
      anyRunning,
      label: j.process_name ?? j.process_id,
      allDoneS,
      detail,
    };
  }

  // "running" only when something actually is — several jobs sitting in the queue behind another field's
  // work are queued, and saying "running" there is the same class of lie as a confident zero badge.
  const label = anyRunning
    ? "Multiple processes running"
    : "Multiple processes queued";
  const detail =
    allDoneS != null
      ? `${fmtEta(allDoneS)} for all`
      : anyRunning
        ? "running"
        : "queued";
  return { count: active.length, anyRunning, label, allDoneS, detail };
}

/* * Per-job tooltip lines: "Covariate build — running, ~20 min" / "Soil map — #2 in line". */
export function jobsSummaryTitle(jobs: FieldJob[], nowMs: number): string {
  return jobs
    .filter(isActiveJob)
    .map((j) => {
      const eta = etaRemainingS(j, nowMs);
      const state =
        j.status === "in_progress"
          ? "running"
          : j.queue_position != null
            ? `#${j.queue_position} in line`
            : "queued";
      return `${j.process_name ?? j.process_id} — ${state}${eta != null ? `, ${fmtEta(eta)}` : ""}`;
    })
    .join("\n");
}
